import { DialogPopupTypes, Constraints } from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
// import { RootStore } from 'mobx-stores/root-store'
import { PaymentDetailsDTO, StoreDTO, UserDTO } from 'models'
import { Api } from 'services'
import { validate } from 'validate.js'
import {
  createStoreConstraints,
  initialStoreDto
} from './add-store-page-store.utils'

export class AddStorePageStore {
  private storeApi: Api['storeApi']
  private rootStore: any

  constructor (rootStore: any) {
    this.storeApi = rootStore.generalStore.api.storeApi
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  // OBSERVABLES
  public store: StoreDTO = initialStoreDto
  public validationErrors: Constraints<StoreDTO> = {}

  // ACTIONS
  private setStore = (store: StoreDTO): void => {
    this.store = store
  }

  private setValidationErrors = (errors: Constraints<StoreDTO>): void => {
    this.validationErrors = errors
  }

  // METHODS
  public clearStore = (): void => {
    this.setStore(initialStoreDto)
    this.setValidationErrors({})
  }

  private setStoreProps = (props: Partial<StoreDTO>): void => {
    this.setStore({ ...this.store, ...props })
  }

  public onStorePropsChange = (props: Partial<StoreDTO>): void => {
    const validationErrors = { ...this.validationErrors }
    Object.keys(props).forEach(key => {
      delete validationErrors[key]
    })

    this.setValidationErrors(validationErrors)
    this.setStoreProps(props)
  }

  public onPaymentDetailsChange = (props: Partial<PaymentDetailsDTO>): void => {
    const validationErrors = { ...this.validationErrors }
    Object.keys(props).forEach(key => {
      delete validationErrors[`paymentDetails.${key}`]
    })

    this.setValidationErrors(validationErrors)
    this.setStoreProps({
      paymentDetails: { ...this.store.paymentDetails, ...props }
    })
  }

  public onDiscardClick = (): void => {
    const {
      generalStore: { openDialogPopup, closeDialogPopup },
      routingStore: { push }
    } = this.rootStore

    openDialogPopup({
      type: DialogPopupTypes.WARNING,
      buttons: [
        {
          children: 'Proceed',
          preset: 'primary',
          onClick: () => {
            push('/stores')
            this.clearStore()
            closeDialogPopup()
          }
        }
      ],
      closeButtonLabel: 'Cancel',
      message: 'Changes you have made will be lost!'
    })
  }

  public onAddStoreClick = async (): Promise<void> => {
    const validationErrors = validate(this.store, createStoreConstraints)

    /** Validate Shopify integration based on which fields are filled */
    if (this.store.shopifyStoreUrl) {
      const errors = { ...validationErrors };

      // Determine integration type based on filled fields
      const hasOldFields = Boolean(this.store.shopifyAccessToken || this.store.shopifyStorefrontToken);
      const hasNewFields = Boolean(this.store.clientId);

      if (!hasOldFields && !hasNewFields) {
        // No integration type selected
        errors.shopifyStoreUrl = ["Please select an integration type and fill in the required fields"];
      } else if (hasOldFields) {
        // Old integration requires: shopifyAccessToken, shopifySecretKey, shopifyStorefrontToken
        if (!this.store.shopifyAccessToken) {
          errors.shopifyAccessToken = ["Shopify access token is required for old integration"];
        }

        if (!this.store.shopifySecretKey) {
          errors.shopifySecretKey = ["Shopify API secret is required for old integration"];
        }

        if (!this.store.shopifyStorefrontToken) {
          errors.shopifyStorefrontToken = ["Shopify Storefront token is required for old integration"];
        }
      } else if (hasNewFields) {
        // New integration requires: shopifySecretKey, clientId
        if (!this.store.shopifySecretKey) {
          errors.shopifySecretKey = ["Shopify API secret is required for new integration"];
        }

        if (!this.store.clientId) {
          errors.clientId = ["Client ID is required for new integration"];
        }
      }

      if (Object.keys(errors).length > Object.keys(validationErrors || {}).length) {
        this.setValidationErrors(errors);
        return;
      }
    }

    if (validationErrors) {
      this.setValidationErrors(validationErrors)
      return
    }

    const storeDto = await this.storeApi.createStore(this.store)
    if (!storeDto) return

    await this.fetchProductsFromShopifyIfNeeded(storeDto.id)

    // Generate all tags for the store
    try {
      await this.storeApi.generateAllTags(storeDto.id)
    } catch (error) {
      const normalizedError =
        error instanceof Error ? error : new Error(JSON.stringify(error))
      this.rootStore.generalStore.handleApiError(normalizedError)
    }

    // const storeHasAssignedUsers = Boolean(this.store?.users?.length)
    // if (storeHasAssignedUsers) {
    //   await asyncForEach(this.store.users, async user => {
    //     const store = await this.storeApi.assignUser(
    //       storeDto.id,
    //       this.rootStore.usersMainStore.getUserById(user.id)
    //     )
    //     if (!store) return
    //   })
    // }

    this.clearStore()
    this.rootStore.routingStore.push('/stores')
    this.rootStore.generalStore.showSuccess('Store created successfully')
  }

  private async fetchProductsFromShopifyIfNeeded (storeId: number | string): Promise<void> {
    // Check if Shopify integration is configured based on which fields are filled
    const hasOldIntegration =
      Boolean(this.store.shopifyStoreUrl) &&
      Boolean(this.store.shopifyAccessToken) &&
      Boolean(this.store.shopifySecretKey) &&
      Boolean(this.store.shopifyStorefrontToken);

    const hasNewIntegration =
      Boolean(this.store.shopifyStoreUrl) &&
      Boolean(this.store.shopifySecretKey) &&
      Boolean(this.store.clientId);

    const hasShopifyConfig = hasOldIntegration || hasNewIntegration;

    if (!hasShopifyConfig) return

    try {
      await this.rootStore.generalStore.api.productApi.getProductsFromShopify(
        Number(storeId)
      )
    } catch (error) {
      const normalizedError =
        error instanceof Error ? error : new Error(JSON.stringify(error))
      this.rootStore.generalStore.handleApiError(normalizedError)
    }
  }

  public onAddUserClick = async (user: UserDTO): Promise<void> => {
    if (!user?.emailAddress) return

    const isUserAlreadyAdded = Boolean(
      this.store.users.find(u => u.emailAddress === user.emailAddress)
    )
    if (isUserAlreadyAdded) return

    const userToAdd = {
      emailAddress: user.emailAddress,
      createdDate: new Date().valueOf()
    }
    this.setStoreProps({ users: [...this.store.users, userToAdd] })
  }

  public onRemoveUserClick = async (user: UserDTO): Promise<void> => {
    if (!user?.emailAddress) return

    this.setStoreProps({
      users: this.store.users.filter(u => u.emailAddress !== user.emailAddress)
    })
  }

  // COMPUTED
  public get addedUsersToStore (): UserDTO[] {
    if (!this.store || !this.store.users) return []
    return this.store.users.map(({ createdDate, emailAddress }) => ({
      ...this.rootStore.usersMainStore.getUserByEmailAddress(emailAddress),
      created: new Date(createdDate).toISOString() // Using 'created' date field in UserDTO to show the addedToStoreDate
    }))
  }
}
